import React, { useState } from "react";
import "./mechanics.css";

export default function Mechanics() {
  const [activeTab, setActiveTab] = useState("utility");
  const [activeTopic, setActiveTopic] = useState(null);

  const rallyTopics = [
    { id: "analog", color: "red", title: "Analog Communications", desc: "Amplitude modulation and demodulation, angle modulation and demodulation, spectra of AM and FM, transmitters, receivers." },
    { id: "info", color: "orange", title: "Information Theory & Random Processes", desc: "Entropy, mutual information, channel capacity theorems, autocorrelation and power spectral density, noise and dB calculations, filtering of random signals." },
    { id: "digital", color: "yellow", title: "Digital Communications", desc: "Sampling, quantizing, coding, PCM, DPCM, multiplexing, digital modulation (ASK, PSK, FSK, QAM), multiple-access techniques (TDMA, FDMA, CDMA), inter-symbol interference, BER and SNR, matched filter receiver." },
    { id: "transmission", color: "bg-green", title: "Transmission Line & Optical Fibers", desc: "Transmission line equations, characteristic impedance, impedance matching, impedance transformation, S-parameters, Smith chart, rectangular and circular waveguides, fiber optic cable, transmitters, receivers, loss calculations." },
    { id: "wave", color: "blue", title: "Wave Propagation & Antenna Theory", desc: "Reflection and refraction, polarization, phase and group velocity, propagation through various media, skin depth, dipole and monopole antennas, linear antenna arrays, antenna parameters, design and calculations." },
    { id: "signals", color: "purple", title: "Signal, Spectra & Signal Processing", desc: "Representation of continuous and discrete-time signals, shifting and scaling properties, linear time-invariant and causal systems, convolution, Fourier series, Fourier Transform, Laplace Transform and z-transform, DTFT, DFT." },
    { id: "data", color: "teal", title: "Data Communications & Computer Networks", desc: "OSI and TCP/IP protocols, circuit and virtual switching, routing protocols, IP addressing, CIDR notation, framing, media access control, ethernet." },
    { id: "advanced", color: "magenta", title: "Advanced Communication", desc: "Microwave and satellite communications, cybersecurity fundamentals, mobile communications." }
  ];

  return (
    <div className="mechanics-container">
      <aside className="mechanics-sidebar">
        <nav className="mechanics-nav">
          <a href="#written">Written Phase</a>
          <a href="#individual">Individual Excellence</a>
          <a href="#powerups">Power-Ups Directory</a>
          <a href="#knockouts">The Knockout Rounds</a>
        </nav>
      </aside>

      <div className="mechanics-content">
        
        {/* SECTION 1: WRITTEN PHASE */}
        <section id="written" className="mech-section">
          <h2>Written Phase</h2>
          <hr className="mech-divider" />
          <p className="section-intro">
            Each participant will take a written examination on the venue during the event. 
            The examination will consist of 3 separate sets:
          </p>

          <div className="stats-row"> 
            <div className="stat-box"><i className="fas fa-check-circle"></i><div><strong>10 Objective Questions</strong><p>3 pts each | 10 mins</p></div></div>
            <div className="stat-box"><i className="fas fa-edit"></i><div><strong>10 Short Solution Questions</strong><p>5 pts each | 20 mins</p></div></div>
            <div className="stat-box"><i className="fas fa-brain"></i><div><strong>5 Difficult Problems</strong><p>10-20 pts each | 30 mins</p></div></div>
          </div>

          <div className="rules-grid">
            <div className="rules-list">
              <h3>Additional Guidelines</h3>
              <ul>
                <li>Answers must be written on the provided answer sheets. Using any other writing sheet is prohibited.</li>
                <li>Answer sheets must be properly labeled by the participant.</li>
                <li>Participants should submit their answer sheets to the proctor on or before the allotted time.</li>
                <li>Non-integer numerical answers should be rounded off to four (4) decimal places.</li>
                <li>Incorrectly spelled answers shall be considered wrong.</li>
              </ul>
            </div>
            <div className="callout-box">
              <h3>Advancement Criteria</h3>
              <ul>
                <li>Only the top twenty (20) contestants will proceed to the Individual Excellence Challenge.</li>
                <li>In case of a tie in the 20th place, a Sudden Death Question will be asked. The first correct answer determines the qualifier.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* SECTION 2: INDIVIDUAL EXCELLENCE */}
        <section id="individual" className="mech-section">
          <h2>Individual Excellence Challenge</h2>
          <hr className="mech-divider" />
          
          <div className="pyramid-container">
            <h3 className="board-title" style={{marginBottom: "20px"}}>RALLY BOARD STRUCTURE</h3>
            <div className="pyramid-row"><div className="pyramid-block pb-top"></div></div>
            <div className="pyramid-row"><div className="pyramid-block pb-orange"></div><div className="pyramid-block pb-blue"></div></div>
            <div className="pyramid-row"><div className="pyramid-block pb-green"></div><div className="pyramid-block pb-purple"></div><div className="pyramid-block pb-blue"></div></div>
            <div className="pyramid-row"><div className="pyramid-block pb-yellow"></div><div className="pyramid-block pb-green"></div><div className="pyramid-block pb-purple"></div><div className="pyramid-block pb-orange"></div></div>
            <div className="pyramid-row"><div className="pyramid-block pb-green"></div><div className="pyramid-block pb-orange"></div><div className="pyramid-block pb-purple"></div><div className="pyramid-block pb-bg-green"></div><div className="pyramid-block pb-blue"></div></div>
          </div>

          <div className="rules-grid">
            <div className="rules-list">
              <h3>1. General Rules</h3>
              <ul>
                <li>Participants divided into 4 groups of 5 players; each group plays in one of 4 preliminary rounds.</li>
                <li>Goal: reach the top tile. The first two (2) players to reach the top advance to the Quarterfinals.</li>
                <li>If no one reaches the top, players with the highest points win.</li>
              </ul>
            </div>
            <div className="rules-list">
              <h3>2. Answering Rules</h3>
              <ul>
                <li>First player to trigger the buzzer earns the right to answer.</li>
                <li><strong>Correct:</strong> Roll dice for tile color and move forward. Choose next question.</li>
                <li><strong>Incorrect:</strong> Remaining players can "steal" once if time remains.</li>
                <li>Players have 5s to answer once the buzzer is pressed.</li>
              </ul>
            </div>
            <div className="rules-list full-width">
              <h3>3. Scoring & System Mechanics</h3>
              <div className="inner-rules-flex">
                <div className="inner-rule">
                  <strong>Tile Breaker Question:</strong>
                  <p>Initiated if landing on an occupied tile. If challenger is correct, players switch positions.</p>
                </div>
                <div className="inner-rule">
                  <strong>Scoring System:</strong>
                  <p>Each tile is worth 2 points. "Lucky Tile" gives +1 move and 2x total points.</p>
                </div>
                <div className="inner-rule">
                  <strong>The Rally Board:</strong>
                  <p>Consists of 45 color-coded questions. Standard (10s limit) vs Challenge (60s limit).</p>
                </div>
              </div>
            </div>
          </div>

          <h3 className="board-title">4. Syllabus Coverage <span style={{fontSize: "14px", color: "#888", fontWeight: "normal"}}>(Click a topic to view details)</span></h3>
          <div className="rally-board-grid">
            {rallyTopics.map((topic) => (
              <div 
                key={topic.id} 
                className={`topic-tag ${topic.color} ${activeTopic?.id === topic.id ? "selected" : ""}`}
                onClick={() => setActiveTopic(topic)}
              >
                {topic.title}
              </div>
            ))}
          </div>
          {activeTopic && (
            <div className="topic-description-box">
              <h4>{activeTopic.title}</h4>
              <p>{activeTopic.desc}</p>
            </div>
          )}

          {/* FIXED: Missing "5. Individual Excellence Power-Ups" section — all 6 power-ups from PDF pp.15–16 were absent */}
          <h3 className="board-title" style={{ marginTop: "40px", marginBottom: "20px" }}>
            5. Individual Excellence Power-Ups
          </h3>
          <div className="callout-box" style={{ marginBottom: "24px" }}>
            <ul>
              <li>At the start of the round, each player will randomly select three (3) power-ups from the designated deck.</li>
              <li>Power-ups may only be used while the Rally Board is visible on screen.</li>
            </ul>
          </div>
          {[
            { name: "Reverse Bias",       icon: "power/Reverse Bias.png",  effect: "Allows the player to move one chosen opponent back by exactly one tile on the board. This power-up cannot be used on players occupying a tri-colored tile." },
            { name: "Resonance Call",   icon: "power/Resonance Call.png", effect: "After an incorrect answer, the player may attempt the same question once more while all steal attempts are disabled." },
            { name: "Voltage Jump",       icon: "power/Voltage Jump.png", effect: "Decreases the time limit by 50%. If correct, the player rolls the dice twice and advances accordingly." },
            { name: "Flux Shift",         icon: "power/Flux Shift.png", effect: "Instead of rolling the dice, the player may force a Tile Breaker with an opponent one tile ahead." },
            { name: "Phase Lock",         icon: "power/Phase Lock.png", effect: "Protects the player from any forced backward movement caused by power-ups until the end of their next turn. Does not prevent Tile Breaker Questions and automatically expires afterward." },
            { name: "Noise Cancellation", icon: "power/Noise Cancellation.png", effect: "Prevents all other players from stealing the current question." },
          ].map(p => (
            <div className="powerup-card" key={p.name}>
              <div className="powerup-icon"><img src={p.icon} alt={p.name}></img></div>
              <div className="powerup-details">
                <h4>{p.name}</h4>
                <p>{p.effect}</p>
              </div>
            </div>
          ))}
        </section>

        {/* SECTION 3: POWER-UPS */}
        <section id="powerups" className="mech-section">
          <h2>Power-Ups Directory</h2>
          <hr className="mech-divider" />
          
          <div className="tabs-container">
            <div className="tabs-header">
              <button className={`tab-btn ${activeTab === "utility" ? "active" : ""}`} onClick={() => setActiveTab("utility")}>Utility</button>
              <button className={`tab-btn ${activeTab === "offdef" ? "active" : ""}`} onClick={() => setActiveTab("offdef")}>Offense/Defense</button>
              <button className={`tab-btn ${activeTab === "ultimate" ? "active" : ""}`} onClick={() => setActiveTab("ultimate")}>Ultimate</button>
            </div>

            <div className="tabs-content">
              {activeTab === "utility" && (
                <div className="powerup-list">
                  {[
                    { name: "Voltage Boost", icon: "power/Voltage Boost.png", effect: "Adds 3 bonus points if correct. Deducts 2 points if incorrect.", get: "Given at the start of the round" },
                    { name: "Current Flow", icon: "power/Current Flow.png", effect: "Awards 5 bonus points after three consecutive correct answers. Canceled on error.", get: "Given at the start of the round" },
                    { name: "Ohmic Delay", icon: "power/Ohmic Delay.png", effect: "Extends the answer time by 30 seconds for the current question.", get: "Gained by answering correctly" },
                    { name: "Power Surge", icon: "power/Power Surge.png", effect: "Doubles the point value of the upcoming question. If wrong, lose next turn.", get: "Given at the start of the round" },
                    { name: "Recharge", icon: "power/Recharge.png", effect: "Adds 3 bonus points if correct. Deducts 2 points if incorrect.", get: "Gained by answering correctly" }
                  ].map(p => (
                    <div className="powerup-card" key={p.name}>
                    <div className="powerup-icon"><img src={p.icon} alt={p.name}></img></div>
                      <div className="powerup-details"><h4>{p.name}</h4><p>{p.effect}</p><small>Obtain: {p.get}</small></div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "offdef" && (
                <div className="powerup-list">
                  {[
                    { name: "Phase Cancel", icon: "power/Phase Cancel.png",  effect: "Nullify one recently activated power-up by the opponent.", get: "Gained by answering correctly" },
                    { name: "Load Transfer", icon: "power/Load Transfer.png",  effect: "Steal one unused power-up from the opponent. Use immediately or save.", get: "Gained by answering correctly" },
                    { name: "Ground Noise", icon: "power/Ground Noise.png",  effect: "The opponent must answer the next question with 50% less time.", get: "Gained by answering correctly" },
                    { name: "Inductive Drain", icon: "power/Inductive Drain.png",  effect: "If the opponent buzzes first and is wrong, 1 pt is deducted.", get: "Gained by answering correctly" },
                    { name: "Topic Lockout", icon: "power/Topic Lockout.png",  effect: "User names one topic that cannot be chosen for the upcoming question.", get: "Gained by answering correctly" },
                    { name: "Load Mismatch", icon: "power/Load Mismatch.png",  effect: "Even if the opponent is correct, user selects the next question instead.", get: "Gained by answering correctly" }
                  ].map(p => (
                    <div className="powerup-card" key={p.name}>
                      <div className="powerup-icon"><img src={p.icon} alt={p.name}></img></div>
                      <div className="powerup-details"><h4>{p.name}</h4><p>{p.effect}</p><small>Obtain: {p.get}</small></div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "ultimate" && (
                <div className="powerup-list">
                  {[
                    { name: "Power Roulette", icon: "power/Power Roulette.png", effect: "Randomly draw any power-up. It is immediately activated.", get: "Gained by answering correctly" },
                    { name: "Ultimate Breaker", icon: "power/Ultimate Breaker.png", effect: "Correct: win round automatically. Incorrect: score reset to zero.", get: "Gained by answering correctly" },
                    { name: "All-In Circuit", icon: "power/All-in Circuit.png",  effect: "Wager points. Correct: gain double. Incorrect: deduct wager amount.", get: "Gained by answering correctly" }
                  ].map(p => (
                    <div className="powerup-card ultimate-gold" key={p.name}>
                    <div className="powerup-icon"><img src={p.icon} alt={p.name}></img></div>
                      <div className="powerup-details"><h4>{p.name}</h4><p>{p.effect}</p><small>Obtain: {p.get}</small></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* SECTION 4: KNOCKOUT ROUNDS */}
        <section id="knockouts" className="mech-section">
          <h2>The Knockout Rounds</h2>
          <hr className="mech-divider" />
          
          <div className="unified-k-box">
            <div className="k-header-main"><h3>Quarter-Final Phase</h3></div>
            <div className="k-grid-internal">
              <div className="k-col">
                <strong>Tournament Pairing</strong>
                <div className="match-pairing-list">
                  <span>Match 1: Random Pairing 1</span>
                  <span>Match 2: Random Pairing 2</span>
                  <span>Match 3: Random Pairing 3</span>
                  <span>Match 4: Random Pairing 4</span>
                </div>
              </div>
              <div className="k-col">
                <strong>Technical Mechanics</strong>
                <ul className="k-details-list">
                  <li>Points: Bronze (2), Silver (3), Gold (5).</li>
                  <li>10 questions per match. No score deductions.</li>
                  <li>First buzzer answers (5s limit). Turn forfeits on error.</li>
                  <li>Decision of judges is final and irrevocable.</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="unified-k-box semi-final-accent">
            <div className="k-header-main"><h3>Semi-Final Round</h3></div>
            <div className="k-grid-internal">
              <div className="k-col">
                <strong>Semi Pairings</strong>
                <div className="match-pairing-list">
                  <span>SF 1: Winner Match 1 vs Winner Match 4</span>
                  <span>SF 2: Winner Match 2 vs Winner Match 3</span>
                </div>
              </div>
              <div className="k-col">
                <strong>Regulation</strong>
                <p>Mechanics remain consistent with the Quarter-Final brief. Higher score wins match.</p>
              </div>
            </div>
          </div>

          <div className="unified-k-box final-round-accent">
            <div className="k-header-main">
              <h3>Grand Final Round</h3>
            </div>
            <div className="k-grid-internal">
              <div className="k-col">
                <strong>Champion Determination</strong>
                <p>The winner will be declared the <strong>Champion of Brodkast ng Talino 2026</strong></p>
                {/* Removed the empty <p>.</p> that was causing the dot */}
              </div>
              <div className="k-col">
                <strong>Final Regulation</strong>
                <p>Tie-breaking questions provided by Quizmaster if needed. No carry-over power-ups.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}